import { useMemo } from 'react';
import { Html5Audio, useVideoConfig, useRemotionEnvironment } from 'remotion';
import {BrowserRenderMediaAudio as MediaAudio} from '@waka/compat/browser-render-media';
import { RequireCachedAsset } from '../../caching/require-cached-asset';
import {
  useAssetStartDelta,
  useIsInExtensionArea,
} from '../../canvas/transition-extension-context';
import { useStablePreferredLocalUrl } from '../../utils/find-asset-by-id';
import { useAssetFromItem } from '../../utils/use-context';
import { volumeFn } from '../../utils/volume-fn';
import { VideoItem } from './video-item-type';

/**
 * 비디오 아이템에서 오디오만 추출하여 재생하는 경량 컴포넌트.
 * 트랙이 hidden 상태일 때 시각적 렌더링 없이 오디오만 재생한다.
 */
export const VideoAudioOnlyLayer = ({
  item,
  trackMuted,
  disableBoundaryMediaPause,
}: {
  item: VideoItem;
  trackMuted: boolean;
  disableBoundaryMediaPause?: boolean;
}) => {
  const { fps } = useVideoConfig();
  const {isClientSideRendering}=useRemotionEnvironment();

  // 트랜지션 확장 영역에서는 오디오 음소거
  const isInExtensionArea = useIsInExtensionArea();
  // target 확장으로 인한 assetStart 조정값
  const assetStartDelta = useAssetStartDelta();

  const volume = useMemo(() => {
    return volumeFn({
      fadeWindow:item.audioFadeWindow,
      fps,
      audioFadeInDurationInSeconds: item.audioFadeInDurationInSeconds,
      audioFadeOutDurationInSeconds: item.audioFadeOutDurationInSeconds,
      durationInFrames: item.durationInFrames,
      decibelAdjustment: item.decibelAdjustment,
    });
  }, [
    fps,
    item.audioFadeWindow,
    item.audioFadeInDurationInSeconds,
    item.audioFadeOutDurationInSeconds,
    item.durationInFrames,
    item.decibelAdjustment,
  ]);

  const asset = useAssetFromItem(item);
  const src = useStablePreferredLocalUrl(asset);

  // target 확장이 있으면 assetStart를 앞으로 조정 (assetStartDelta는 음수)
  const startFrom = (item.videoStartFromInSeconds + assetStartDelta) * fps;

  return (
    <RequireCachedAsset asset={asset}>
      {isClientSideRendering ? <MediaAudio src={src} trimBefore={startFrom} volume={volume} playbackRate={item.playbackRate} muted={trackMuted || isInExtensionArea || item.muteAudio === true || (item.playbackRate < 1 && asset.type === 'video' && asset.hasAudioTrack === false)}/> : <Html5Audio
        crossOrigin="anonymous"
        pauseWhenBuffering={!disableBoundaryMediaPause}
        useWebAudioApi
        volume={volume}
        trimBefore={startFrom}
        src={src}
        muted={trackMuted || isInExtensionArea || item.muteAudio === true}
        playbackRate={item.playbackRate}
      />}
    </RequireCachedAsset>
  );
};
